import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { checkInputErrorRequired, formatDate, useInput, useSelect, useRadio } from '@chilecompra/react-kit'
import { Input, Radio, Select, Toast, Typography } from '@chilecompra/react-kit/components'
import { BINARY_BUDGET_TYPE, CURRENCY_SIGNS, CURRENCY_SIGNS_DICTIONARY } from '../../config/settings/constants'
import { useImperativeFormRef } from './QuoteStepOne.hooks'
import { checkInputErrorMinValue, checkMaximumAmount } from '../../modules/utils/checkers'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import { AmountPrefix, Grid, RowRadio } from './QuoteStepOne.styles'

/**
 * The EstimatedBudget's component.
 */
const EstimatedBudget = props => {
  const {
    budget: { currency, totalAmount },
    exchangeRates,
    formRef,
    isDownToTiny,
    maxAmountsUTMs,
    serverDate
  } = props
  const { authInfo } = useSelector(state => state.authorizationProvider)
  const [amountInfo, setAmountInfo] = useState('')
  const [currencySymbol, setCurrencySymbol] = useState(CURRENCY_SIGNS[currency || 'CLP'])
  const [currencyType, setCurrencyType] = useState(CURRENCY_SIGNS_DICTIONARY[currency || 'CLP'])
  const [currentExchangeRateChileanValue, setCurrentExchangeRateChileanValue] = useState(0)
  const [currentExchangeRateValue, setCurrentExchangeRateValue] = useState('')
  const [formattedServerDate, setFormattedServerDate] = useState('')
  const [showAmountInfo, setShowAmountInfo] = useState(false)
  const {
    error: exchangeRateError,
    value: exchangeRateValue,
    onChange: exchangeRateOnChange
  } = useSelect({
    initialValue: currency || 'CLP',
    changeCallback: currencyType => setCurrencyType(CURRENCY_SIGNS_DICTIONARY[currencyType]),
    overwriteCallback: () => currency
  })
  const {
    error: amountError,
    value: amountValue,
    onChange: amountChange,
    onError: updateAmountError,
    setValue: setAmountValue
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(),
      checkMaximumAmount(exchangeRates, currencyType, maxAmountsUTMs),
      checkInputErrorMinValue(1)
    ].filter(Boolean),
    errorDependencies: [exchangeRateValue],
    initialValue: totalAmount,
    overwriteCallback: () => totalAmount
  })
  const [isFocused, setisFocused] = useState()
  const focusedFunction = () => {
    setisFocused(true)
  }
  const unfocusedFunction = () => {
    setisFocused(false)
  }

  const { value: valueBudgetTypeSelected, onChange } = useRadio({ initialValue: 'Estimado' })

  useEffect(() => {
    if (maxAmountsUTMs === undefined) return

    setAmountInfo(`El monto no podrá superar las ${maxAmountsUTMs} UTM.`)
  }, [maxAmountsUTMs])

  useEffect(() => {
    if (serverDate === '') return

    setFormattedServerDate(formatDate(serverDate, 'dd-MM-yyyy'))
  }, [serverDate])

  useEffect(() => {
    if (!totalAmount) {
      return
    }

    updateAmountError()
  }, [amountValue, currencyType, exchangeRates, maxAmountsUTMs, totalAmount])

  useEffect(() => {
    if (currency !== undefined) {
      setCurrencyType(currency)
    }
  }, [currency])

  useEffect(() => {
    currencyType === CURRENCY_SIGNS_DICTIONARY.CLP ? setShowAmountInfo(false) : setShowAmountInfo(true)
    setCurrencySymbol(CURRENCY_SIGNS[currencyType])
    let aux
    const currentExchangeRate = exchangeRates.find(exchangeRate => exchangeRate.value === currencyType)
    if (exchangeRateValue === CURRENCY_SIGNS_DICTIONARY.CLP) {
      aux = amountValue.replace(/[^0-9]/g, '')
    } else {
      aux = amountValue.replace(/[^0-9,]/g, '').replace(/,/g, '.')
    }

    const validationAfterComma = aux.split('.')
    if (aux.slice(-1) === ',' || aux.slice(-1) === '.') {
      if (validationAfterComma.length > 2) {
        aux = aux.slice(0, -1)
      } else if (validationAfterComma.length === 2 && validationAfterComma[1] === '' && amountValue.slice(-1) === ',') {
        return
      }
    }

    setCurrentExchangeRateValue(
      formatTypeCurrency(currencyType, currentExchangeRate?.clpAmount).replace(/[^\d,.]/g, '')
    )

    if (isNaN(aux) || aux === '') {
      setAmountValue('')
      setCurrentExchangeRateChileanValue(0)
      return
    }

    setAmountValue(formatTypeCurrency(currencyType, aux))
    setCurrentExchangeRateChileanValue(
      formatTypeCurrency('CLP', currentExchangeRate?.clpAmount * aux).replace(/[^\d,.]/g, '')
    )
  }, [amountValue, currencyType])

  useImperativeFormRef(
    formRef,
    () => ({ amountError, amountValue, exchangeRateError, exchangeRateValue, valueBudgetTypeSelected }),
    [amountError, amountValue, exchangeRateError, exchangeRateValue, valueBudgetTypeSelected]
  )

  return (
    <Grid container margin="24px 0">
      <Grid container item sm direction="column" padding="2px 32px 0 0" xs={12}>
        <Typography variant="body2" fontWeight="bold">
          Presupuesto Referencial
        </Typography>
        <Typography fontWeight="regular" color="black1" variant="body2">
          Monto referencial, los proveedores cotizarán en la moneda seleccionada.
        </Typography>
      </Grid>
      <Grid container item sm={8} xs={12} direction="column">
        <Grid container item sm={12} xs={12}>
          <RowRadio>
            <Radio
              direction="horizontal"
              disabled={authInfo.hasObserverRole}
              label="Tipo de presupuesto"
              onChange={onChange}
              options={BINARY_BUDGET_TYPE}
              required
              value={valueBudgetTypeSelected}
              onFocus={focusedFunction}
              onBlur={unfocusedFunction}
              style={{
                border: isFocused ? '1px solid red' : '0',
                borderRadius: isFocused ? '8px' : '0'
              }}
            />
          </RowRadio>
          <Grid container item sm={6} padding={isDownToTiny ? '16px 0 0' : '0'}>
            <Input
              disabled={authInfo.hasObserverRole}
              error={amountError}
              info={amountInfo}
              label="Monto"
              onChange={amountChange}
              prefix={<AmountPrefix>{currencySymbol}</AmountPrefix>}
              value={amountValue}
            />
          </Grid>
          <Grid container item padding={isDownToTiny ? '10px 0 0' : '0 0 0 16px'} sm={6}>
            <Select
              disabled={authInfo.hasObserverRole}
              error={exchangeRateError}
              label="Tipo de moneda"
              onChange={exchangeRateOnChange}
              options={exchangeRates}
              value={exchangeRateValue}
            />
          </Grid>
          {showAmountInfo && (
            <Grid container item padding={isDownToTiny ? '0' : '12px 0 0'} xs={12}>
              <Toast padding="4px 16px" width="100%">
                <Typography fontWeight="bold" variant="body2">
                  Monto referencial calculado en pesos {currentExchangeRateChileanValue}
                </Typography>
                <Typography fontSize="12px" fontWeight="regular" variant="body2">
                  Tipo de cambio: 1 {currencyType} = {currentExchangeRateValue} CLP con fecha {formattedServerDate}.
                </Typography>
              </Toast>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

EstimatedBudget.defaultProps = {
  budget: {},
  exchangeRates: [],
  formRef: {
    current: {}
  },
  isDownToTiny: false
}

EstimatedBudget.propTypes = {
  budget: PropTypes.shape({
    currency: PropTypes.string,
    totalAmount: PropTypes.number
  }),
  exchangeRates: PropTypes.arrayOf(
    PropTypes.shape({
      clpAmount: PropTypes.number,
      decimals: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string
    })
  ),
  formRef: PropTypes.shape({
    current: PropTypes.shape(Object)
  }),
  isDownToTiny: PropTypes.bool,
  maxAmountsUTMs: PropTypes.number.isRequired,
  serverDate: PropTypes.string.isRequired
}

export default EstimatedBudget
